import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import {
	ItemPopover,
	ItemPopoverService,
	PopoverResponseMap,
	Promotion,
	PromotionResponse,
	PromotionType
} from '@interfaces'
import { TranslateService } from '@ngx-translate/core'
import { BehaviorSubject, map, Observable } from 'rxjs'
import { buildConfigQueryParams } from './config-query-params.helper'
import { hasConfigTextSearchMinLength } from './config-text-search.helper'
import { normalizePaginatedResponse } from '../paginated-response.helper'

export interface PromotionsFilters {
	active?: boolean
	search?: string
}

export const PROMOTION_SORT_FIELDS = ['createDate', 'description', 'type', 'dateStart'] as const
export const PROMOTION_SORT_DIRECTIONS = ['asc', 'desc'] as const

export type PromotionSortField = (typeof PROMOTION_SORT_FIELDS)[number]
export type PromotionSortDirection = (typeof PROMOTION_SORT_DIRECTIONS)[number]

export interface PromotionSort {
	sortField?: PromotionSortField | string | null
	sortDirection?: PromotionSortDirection | string | null
}

export interface NormalizedPromotionSort {
	sortField: PromotionSortField
	sortDirection: PromotionSortDirection
}

export const DEFAULT_PROMOTION_SORT: NormalizedPromotionSort = {
	sortField: 'createDate',
	sortDirection: 'desc'
}

const isPromotionSortField = (value: string): value is PromotionSortField =>
	(PROMOTION_SORT_FIELDS as readonly string[]).includes(value)
const isPromotionSortDirection = (value: string): value is PromotionSortDirection =>
	(PROMOTION_SORT_DIRECTIONS as readonly string[]).includes(value)
const normalizePromotionSortField = (value: string): PromotionSortField | null => {
	if (value === 'createdate' || value === 'create_date') return 'createDate'
	if (value === 'datestart' || value === 'date_start') return 'dateStart'
	if (isPromotionSortField(value)) return value
	return null
}

export const normalizePromotionSort = (sort: PromotionSort = {}): NormalizedPromotionSort => {
	const normalizedField = String(sort.sortField ?? '')
		.trim()
		.toLowerCase()
	const normalizedDirection = String(sort.sortDirection ?? '')
		.trim()
		.toLowerCase()
	const sortField = normalizePromotionSortField(normalizedField)

	return {
		sortField: sortField ?? DEFAULT_PROMOTION_SORT.sortField,
		sortDirection: isPromotionSortDirection(normalizedDirection)
			? normalizedDirection
			: DEFAULT_PROMOTION_SORT.sortDirection
	}
}

const normalizePromotionSearch = (value?: string): string | undefined => {
	if (typeof value !== 'string') return undefined

	const normalizedValue = value.trim()
	if (!normalizedValue) return undefined
	if (!hasConfigTextSearchMinLength(normalizedValue)) return undefined
	return normalizedValue
}

@Injectable({
	providedIn: 'root'
})
export class PromotionsService implements ItemPopoverService {
	private http = inject(HttpClient)
	private translateService = inject(TranslateService)
	private url = '/offers'

	// Observable para la creacion de promociones
	private _creatingPromotion: boolean = false
	private creatingPromotionSubject = new BehaviorSubject<boolean>(false)
	public creatingPromotion$ = this.creatingPromotionSubject.asObservable()

	// Trae una lista paginada de las promociones
	getPromotions(page = 1, size = 15, filters: PromotionsFilters & PromotionSort = {}): Observable<PromotionResponse> {
		const normalizedSort = normalizePromotionSort(filters)
		const params = buildConfigQueryParams({
			page,
			size,
			...filters,
			...normalizedSort,
			search: normalizePromotionSearch(filters.search)
		})

		return this.http.get<PromotionResponse>(this.url, { params }).pipe(
			map(response => {
				const normalizedResponse = normalizePaginatedResponse(response)

				return {
					...normalizedResponse,
					content: normalizedResponse.content.map(content => ({
						...content,
						popover: {
							plans: {
								content: content.offerPlans
									.filter(offerPlan => offerPlan.active)
									.map(({ plan }) => ({
										id: plan.id,
										value: plan.translations[0].name,
										active: false
									})),
								translation: content.offerPlans
									.filter(offerPlan => offerPlan.active)
									.map(({ plan }) => this.translateService.instant(plan.translations[0].name))
									.join(', ')
							},
							applyTypes: {
								content: content.offerApplyTypes
									.filter(applyType => applyType.active)
									.map(({ applyType }) => ({
										id: applyType.id,
										value: applyType.description,
										active: false
									})),
								translation: content.offerApplyTypes
									.filter(applyType => applyType.active)
									.map(({ applyType }) => this.translateService.instant(applyType.description))
									.join(', ')
							},
							userTypes: {
								content: content.offerUserTypes
									.filter(userType => userType.active)
									.map(({ userType }) => ({
										id: userType.id,
										value: userType.description,
										active: false
									})),
								translation: content.offerUserTypes
									.filter(userType => userType.active)
									.map(({ userType }) => this.translateService.instant(userType.description))
									.join(', ')
							}
						}
					}))
				}
			})
		)
	}

	// Recoge los tipos de aplicación
	getPromotionApplyTypes(): Observable<PromotionType[]> {
		return this.http.get<PromotionType[]>(`${this.url}/applyTypes`)
	}

	// Recoge los tipos de pago
	getPromotionPaymentTypes(): Observable<PromotionType[]> {
		return this.http.get<PromotionType[]>(`${this.url}/paymentTypes`)
	}

	// Recoge los tipos de usuarios
	getPromotionUserTypes(): Observable<PromotionType[]> {
		return this.http.get<PromotionType[]>(`${this.url}/userTypes`)
	}

	// Crea una nueva promoción
	createPromotion(promotion: Promotion): Observable<Promotion> {
		return this.http.post<Promotion>(this.url, promotion)
	}

	// Actualiza una promoción existente
	updatePromotion(promotion: Promotion): Observable<Promotion> {
		return this.http.put<Promotion>(this.url, promotion)
	}

	// Activa o desactiva las promociones especificadas
	enableDisablePromotions(promotionIds: number[], active: boolean): Observable<boolean> {
		return this.http.put<boolean>(`${this.url}/status?active=${active}`, promotionIds)
	}

	// Elimina las promociones especificadas
	deletePromotions(promotionIds: number[]): Observable<boolean> {
		return this.http.delete<boolean>(this.url, { body: promotionIds })
	}

	// Alterna el estado global de creación de promociones
	togleCreatingPromotion() {
		this._creatingPromotion = !this._creatingPromotion
		this.creatingPromotionSubject.next(this._creatingPromotion)
	}

	// Marca el estado global de creación de promociones como false
	resetCreatingState() {
		this._creatingPromotion = false
		this.creatingPromotionSubject.next(this._creatingPromotion)
	}

	getCalculatedId(): Observable<number> {
		return this.http.get<number>(`${this.url}/provisionalCode`)
	}

	getItemPopoverData(_page: number, _size: number, type?: 'APPLY' | 'USERS'): Observable<PopoverResponseMap> {
		if (type === 'APPLY') {
			return this.getPromotionApplyTypes().pipe(map(response => this.getMappedPromotionType(response)))
		} else {
			return this.getPromotionUserTypes().pipe(map(response => this.getMappedPromotionType(response)))
		}
	}

	getMappedPromotionType(response: PromotionType[]) {
		return {
			number: 1,
			numberOfElements: response.length,
			totalElements: response.length,
			totalPages: 1,
			content: response.map(
				({ id, description }): ItemPopover => ({
					id: id,
					value: description,
					active: false
				})
			)
		}
	}
}
